/**
 * SuccessPage â€” payment confirmation receipt.
 *
 * This is **optimistic** per UI_REWRITE_HANDOFF Â§5.4 â€” Stripe says the
 * payment succeeded, but the backend webhook hasn't necessarily posted
 * yet. We don't poll `/payment/sessions/{id}` because that endpoint
 * isn't built; we trust Stripe's signal and show the receipt immediately.
 *
 * The patient lands here via `navigate('/success', { state: {...} })`
 * from `PayPage`. The state carries everything the receipt needs to
 * render without another API call. If a patient hits /success directly
 * without navigation state (bookmarked URL, refresh), we show an empty
 * state and link them back to /pay.
 *
 * No in-app email-receipt or PDF-download UI per UI_REWRITE_HANDOFF Â§8 â€”
 * SendGrid emails the official receipt automatically. We retain a single
 * Print action because it's a free browser primitive and patients
 * occasionally want a paper copy.
 */
import { useLocation, useNavigate, type Location } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, MailCheck, Printer } from 'lucide-react';

import { Alert, Badge, EmptyState } from '@tensaw/design-system/feedback';
import { Button } from '@tensaw/design-system/primitives';
import { Card, CardContent } from '@tensaw/design-system/layout';
import { Stepper } from '@tensaw/design-system/navigation';

import { format } from '../../lib/money';
import { PortalLayout } from '../../PortalLayout';
import { CURRENT_BRAND } from '../../lib/loginTheme';

const STEPS = [
  { label: 'Sign in' },
  { label: 'Review & pay' },
  { label: 'Confirmation' },
];

interface SuccessState {
  paymentSessionId: string;
  amountCents: number;
  clinicName: string | null;
  clinicPhone: string | null;
  patientName: string;
  /**
   * Set when the payment originated from the precollect flow
   * (`/checkin/:appointmentId`). Used to route "Back to my account"
   * back to that URL rather than the generic /pay. Null/undefined for
   * balance-pay arrivals per QA BUG-013.
   */
  appointmentId?: number | null;
}

export function SuccessPage() {
  const location = useLocation() as Location<SuccessState | null>;
  const navigate = useNavigate();
  const state = location.state;

  // Direct hit (no navigation state) â€” common when someone refreshes the
  // success URL or visits via bookmark. Send them back to /pay where
  // they can see their (now updated) balance.
  if (!state) {
    return (
      <PortalLayout>
        <Card>
          <CardContent className="p-8">
            <EmptyState
              title="No recent payment to show"
              description="Receipts are generated right after you pay. Open your account to see your balance."
              action={
                <Button onClick={() => { navigate('/pay'); }}>Go to my account</Button>
              }
            />
          </CardContent>
        </Card>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout
      clinicName={state.clinicName ?? undefined}
      clinicPhone={CURRENT_BRAND.billingPhoneDisplay ?? undefined}
    >
      <Stepper currentStep={3} steps={STEPS} className="mb-6" />
      <Receipt state={state} />
    </PortalLayout>
  );
}

function Receipt({ state }: { state: SuccessState }) {
  const navigate = useNavigate();
  const handlePrint = () => { window.print(); };
  const backHref =
    state.appointmentId != null
      ? `/checkin/${state.appointmentId}`
      : '/pay';
  const handleBack = () => { navigate(backHref); };

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      {/* Hero confirmation */}
      <Card>
        <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-700">
            <CheckCircle2 className="h-9 w-9" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Thank you, {firstNameOf(state.patientName)}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Your payment of{' '}
              <span className="font-semibold text-foreground">{format(state.amountCents)}</span>{' '}
              {state.clinicName ? `to ${state.clinicName}` : 'to your clinic'} was received.
            </p>
          </div>
          <Badge variant="success">Payment confirmed</Badge>
        </CardContent>
      </Card>

      {/* What happens next â€” hidden on print */}
      <div className="print:hidden">
        <Alert variant="info" icon={<MailCheck className="h-4 w-4" />} title="What happens next">
          We've emailed a receipt to the address on file. Your updated
          balance will appear the next time you sign in (it can take a
          minute or two for the payment to post).
        </Alert>
      </div>

      {/* Receipt details */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-base font-semibold text-foreground">Receipt</h2>
          </div>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 px-6 py-5 text-sm sm:grid-cols-2">
            <ReceiptRow label="Patient" value={state.patientName} />
            <ReceiptRow
              label="Clinic"
              value={state.clinicName ?? 'â€”'}
            />
            <ReceiptRow label="Amount paid" value={format(state.amountCents)} emphasis />
            <ReceiptRow
              label="Confirmation"
              value={state.paymentSessionId}
              mono
            />
          </dl>
        </CardContent>
      </Card>

      {/* Action row â€” both buttons hidden on print */}
      <div className="flex justify-center gap-3 print:hidden">
        <Button
          variant="outline"
          leadingIcon={<Printer className="h-4 w-4" />}
          onClick={handlePrint}
        >
          Print this page
        </Button>
      </div>

      <div className="flex justify-center print:hidden">
        <Button onClick={handleBack} leadingIcon={<ArrowLeft className="h-4 w-4" />}>
          Back to my account
        </Button>
      </div>
    </div>
  );
}

function ReceiptRow({
  label,
  value,
  emphasis,
  mono,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      {/*
        Per QA BUG-008: receipt labels were rendered with
        text-muted-foreground (~zinc-400 in this design system), which
        fails WCAG AA on the receipt card's white-ish background. Move
        to a token that lands closer to zinc-600 â€” keeping uppercase
        + tracking so it still reads as a label, just legibly.
      */}
      <dt className="text-[11px] font-medium uppercase tracking-wider text-zinc-600 dark:text-zinc-300">
        {label}
      </dt>
      <dd
        className={[
          emphasis ? 'text-base font-semibold text-foreground' : 'text-foreground',
          mono ? 'font-mono text-xs' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </dd>
    </div>
  );
}

function firstNameOf(fullName: string): string {
  const first = fullName.trim().split(/\s+/)[0];
  return first || 'patient';
}
